// src/pages/AdminProjelerPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, Edit2, Trash2, X, Star, ChevronUp, ChevronDown, Search,
  Image as ImageIcon, MoveLeft, MoveRight, Maximize2, Minimize2
} from "lucide-react";
import Modal from "../../../components/Common/Modal/Modal";
import { postRequest } from "../../../api/postRequest";
import { postFormRequest } from "../../../api/postFormRequest";

/* ===========================
   Tipler
=========================== */
interface ApiResponse<T> {
  success: boolean;
  errorCode?: string;
  errorDetail?: string;
  data?: T;
}

interface ProjeCardDto {
  id: number;
  baslik: string;
  aciklama?: string | null;
  kapakFotoUrl?: string | null;
  siraNo: number;
  aktifMi: boolean;
  olusturmaTarihi: string;
  guncellemeTarihi?: string | null;
}

interface ProjeDetailDto extends ProjeCardDto {
  fotoGaleriJson?: string | null;
  yapimAsamalariJson?: string | null;
  etiketlerJson?: string | null;
}

interface ProjeListeRequest {
  q?: string;
  aktifMi?: boolean | null;
  page: number;
  pageSize: number;
  sort?: "sira" | "yeni" | "eski" | string;
}
interface ProjeListeResponse {
  items: ProjeCardDto[];
  total: number;
  page: number;
  pageSize: number;
}

type Fit = "cover" | "contain";

type GalleryImageJson = { url: string; alt?: string; siraNo?: number; fit?: Fit };
type StepImageJson = GalleryImageJson;
type StepJson = { adim: string; aciklama: string; tarih?: string | null; fotolar?: StepImageJson[] };

interface StepUI {
  adim: string;
  aciklama: string;
  tarih?: string;
  gallery: StepGalleryItem[];
}

type GalleryItemNew = {
  id: string;
  file: File;
  preview: string;
  alt: string;
  fit: Fit;
  isNew: true;
};
type GalleryItemExisting = {
  id: string;
  url: string;
  alt: string;
  fit: Fit;
  isNew: false;
};
type GalleryItem = GalleryItemNew | GalleryItemExisting;

type StepGalleryItemNew = {
  id: string;
  file: File;
  preview: string;
  alt: string;
  fit: Fit;
  isNew: true;
};
type StepGalleryItemExisting = {
  id: string;
  url: string;
  alt: string;
  fit: Fit;
  isNew: false;
};
type StepGalleryItem = StepGalleryItemNew | StepGalleryItemExisting;

/* ===========================
   Yardımcılar
=========================== */
const parseJsonArray = <T,>(json?: string | null): T[] => {
  if (!json) return [];
  try {
    const data = JSON.parse(json);
    return Array.isArray(data) ? (data as T[]) : [];
  } catch {
    return [];
  }
};
const stringify = (obj: unknown) => JSON.stringify(obj ?? null);
const isValidUrl = (s?: string | null) => !!s && /^https?:\/\//i.test(s);

/* ===========================
   Sayfa
=========================== */
const AdminProjelerPage: React.FC = () => {
  /* ------ Liste ------ */
  const [list, setList] = useState<ProjeCardDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [q, setQ] = useState("");
  const [aktifFilter, setAktifFilter] = useState<null | boolean>(null);
  const [sort, setSort] = useState<"sira" | "yeni" | "eski">("yeni");

  /* ------ Modal & Form ------ */
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [activeTab, setActiveTab] = useState<"genel" | "galeri" | "asamalar">("genel");
  const saveRef = useRef<HTMLButtonElement>(null);

  const [form, setForm] = useState<{
    id?: number;
    baslik: string;
    aciklama?: string;
    kapakFotoUrl?: string;
    siraNo: number;
    aktifMi: boolean;
    etiketlerInput: string;
  }>({
    baslik: "",
    aciklama: "",
    kapakFotoUrl: "",
    siraNo: 0,
    aktifMi: true,
    etiketlerInput: "",
  });

  /* ------ Proje Galerisi ------ */
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [coverIndex, setCoverIndex] = useState<number | null>(null);
  const [removedUrls, setRemovedUrls] = useState<string[]>([]);

  /* ------ Yapım Aşamaları ------ */
  const [steps, setSteps] = useState<StepUI[]>([]);

  /* ------ Listeleme ------ */
  const fetchList = async () => {
    setLoading(true);
    try {
      const body: ProjeListeRequest = {
        q: q.trim() || undefined,
        aktifMi: aktifFilter === null ? undefined : aktifFilter,
        page, pageSize, sort,
      };
      const res = await postRequest<ProjeListeRequest, ApiResponse<ProjeListeResponse>>("/Projeler/listele", body);
      if (res.success && res.data) { setList(res.data.items); setTotal(res.data.total); }
      else { setList([]); setTotal(0); }
    } catch (err: any) {
      alert(err?.message || "Listeleme hatası");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [page, pageSize, sort]);

  /* ------ Modal Aç/Kapat ------ */
  const openAdd = () => {
    setIsEdit(false);
    setActiveTab("genel");
    setForm({ baslik: "", aciklama: "", kapakFotoUrl: "", siraNo: 0, aktifMi: true, etiketlerInput: "" });
    setGallery([]);
    setCoverIndex(null);
    setRemovedUrls([]);
    setSteps([{ adim: "Başlangıç", aciklama: "", tarih: "", gallery: [] }]);
    setModalOpen(true);
  };

  const openEdit = async (p: ProjeCardDto) => {
    setIsEdit(true);
    setActiveTab("genel");
    try {
      const res = await postRequest<{ id: number }, ApiResponse<ProjeDetailDto>>("/Projeler/detay", { id: p.id });
      if (!res.success || !res.data) throw new Error(res.errorDetail || "Detay alınamadı");
      const d = res.data!;

      // Etiketler
      const tags = parseJsonArray<string>(d.etiketlerJson);
      const etiketlerInput = tags.join(", ");

      // Galeri
      const gArr = parseJsonArray<GalleryImageJson>(d.fotoGaleriJson).filter(x => x?.url);
      gArr.sort((a, b) => (a.siraNo ?? 0) - (b.siraNo ?? 0));
      const gal: GalleryItemExisting[] = gArr.map((g, i) => ({
        id: `ex_${i}_${g.url}`,
        url: g.url,
        alt: g.alt || "",
        fit: (g.fit || "contain") as Fit,
        isNew: false as false,
      }));
      
      // Kapak
      let cover = null as number | null;
      if (isValidUrl(d.kapakFotoUrl)) {
        const idx = gal.findIndex(x => !("file" in x) && x.url === d.kapakFotoUrl);
        cover = idx >= 0 ? idx : (gal.length ? 0 : null);
      } else if (gal.length) cover = 0;

      // Aşamalar
      const st = parseJsonArray<StepJson>(d.yapimAsamalariJson);
      const stepsUI: StepUI[] = (st.length ? st : [{ adim: "Başlangıç", aciklama: "", tarih: null, fotolar: [] }])
        .map((s, si) => ({
          adim: s.adim || "",
          aciklama: s.aciklama || "",
          tarih: s.tarih || "",
          gallery: (s.fotolar || [])
            .sort((a, b) => (a.siraNo ?? 0) - (b.siraNo ?? 0))
            .map((img, ii): StepGalleryItemExisting => ({
              id: `sx_${si}_${ii}_${img.url}`,
              url: img.url,
              alt: img.alt || "",
              fit: (img.fit || "contain") as Fit,
              isNew: false as false,
            }))
        }));

      setForm({
        id: d.id, baslik: d.baslik,
        aciklama: d.aciklama || "",
        kapakFotoUrl: d.kapakFotoUrl || "",
        siraNo: d.siraNo, aktifMi: d.aktifMi,
        etiketlerInput
      });
      setGallery(gal);
      setCoverIndex(cover);
      setRemovedUrls([]);
      setSteps(stepsUI);
      setModalOpen(true);
    } catch (err: any) {
      alert(err?.message || "Detay yüklenirken hata");
    }
  };

  const closeModal = () => setModalOpen(false);

  /* ===========================
     Galeri İşlemleri (Ana)
  =========================== */
  const onSelectGalleryFiles = (files: FileList | null) => {
    if (!files) return;
    const arr: GalleryItemNew[] = Array.from(files).map((f, i) => ({
      id: `new_${Date.now()}_${i}`,
      file: f,
      preview: URL.createObjectURL(f),
      alt: "",
      fit: "contain" as Fit,
      isNew: true as true,
    }));
  
    setGallery((prev) => {
      const next: GalleryItem[] = [...prev, ...arr];
      if (next.length && coverIndex === null) setCoverIndex(0);
      return next;
    });
  };
  
  const removeGalleryItem = (idx: number) => {
    setGallery(prev => {
      const item = prev[idx];
      if (item && !item.isNew) setRemovedUrls(r => [...r, item.url]);
      const next = [...prev];
      next.splice(idx, 1);
      if (coverIndex !== null) {
        if (idx === coverIndex) setCoverIndex(next.length ? 0 : null);
        else if (idx < coverIndex) setCoverIndex(coverIndex - 1);
      }
      return next;
    });
  };
  const moveGallery = (idx: number, dir: -1 | 1) => {
    setGallery(prev => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      const t = next[idx]; next[idx] = next[j]; next[j] = t;
      if (coverIndex === idx) setCoverIndex(j); else if (coverIndex === j) setCoverIndex(idx);
      return next;
    });
  };
  const toggleGalleryFit = (idx: number) => {
    setGallery(prev => {
      const next = [...prev];
      const it = next[idx];
      const newFit = (it.fit === "cover") ? "contain" : "cover";
      // @ts-ignore
      next[idx].fit = newFit;
      return next;
    });
  };

  /* ===========================
     Yapım Aşamaları + Foto
  =========================== */
  const addStep = () => setSteps(prev => [...prev, { adim: "", aciklama: "", tarih: "", gallery: [] }]);
  const removeStep = (idx: number) => setSteps(prev => prev.filter((_, i) => i !== idx));
  const moveStep = (idx: number, dir: -1 | 1) =>
    setSteps(prev => {
      const next = [...prev];
      const j = idx + dir; if (j < 0 || j >= next.length) return prev;
      const t = next[idx]; next[idx] = next[j]; next[j] = t;
      return next;
    });
  const updateStepField = (idx: number, field: keyof StepUI, val: string) =>
    setSteps(prev => {
      const next = [...prev];
      // @ts-ignore
      next[idx][field] = val;
      return next;
    });

  const onSelectStepFiles = (stepIndex: number, files: FileList | null) => {
    if (!files) return;
    const arr: StepGalleryItemNew[] = Array.from(files).map((f, i) => ({
      id: `snew_${stepIndex}_${Date.now()}_${i}`,
      file: f,
      preview: URL.createObjectURL(f),
      alt: "",
      fit: "contain" as Fit,
      isNew: true as true,
    }));
  
    setSteps((prev) => {
      const next = [...prev];
      const merged: StepGalleryItem[] = [...next[stepIndex].gallery, ...arr];
      next[stepIndex] = { ...next[stepIndex], gallery: merged };
      return next;
    });
  };
    
  const removeStepPhoto = (stepIndex: number, idx: number) => {
    setSteps(prev => {
      const next = [...prev];
      const item = next[stepIndex].gallery[idx];
      if (item && !item.isNew) setRemovedUrls(r => [...r, item.url]);
      next[stepIndex] = {
        ...next[stepIndex],
        gallery: next[stepIndex].gallery.filter((_, i) => i !== idx)
      };
      return next;
    });
  };

  const moveStepPhoto = (stepIndex: number, idx: number, dir: -1 | 1) => {
    setSteps(prev => {
      const next = [...prev];
      const g = [...next[stepIndex].gallery];
      const j = idx + dir; if (j < 0 || j >= g.length) return prev;
      const t = g[idx]; g[idx] = g[j]; g[j] = t;
      next[stepIndex] = { ...next[stepIndex], gallery: g };
      return next;
    });
  };

  const toggleStepPhotoFit = (stepIndex: number, idx: number) => {
    setSteps(prev => {
      const next = [...prev];
      const g = [...next[stepIndex].gallery];
      const it = g[idx];
      const newFit = (it.fit === "cover") ? "contain" : "cover";
      // @ts-ignore
      g[idx].fit = newFit;
      next[stepIndex] = { ...next[stepIndex], gallery: g };
      return next;
    });
  };

  /* ===========================
     Upload & Cleanup
  =========================== */
  const uploadGalleryNew = async (): Promise<string[]> => {
    const newFiles = gallery.filter(g => g.isNew) as Extract<GalleryItem, { isNew: true }>[];
    if (!newFiles.length) return [];
    const fd = new FormData();
    newFiles.forEach(f => fd.append("Dosyalar", f.file));
    const res = await postFormRequest<ApiResponse<{ yuklenenFotoUrlListesi: string[] }>>("/Fotograf/yukle", fd);
    if (!res.success || !res.data?.yuklenenFotoUrlListesi?.length) {
      throw new Error(res.errorDetail || "Galeri foto yükleme başarısız");
    }
    return res.data.yuklenenFotoUrlListesi;
  };

  const uploadStepNew = async (stepIndex: number): Promise<string[]> => {
    const files = steps[stepIndex].gallery.filter(g => g.isNew) as Extract<StepGalleryItem, { isNew: true }>[];
    if (!files.length) return [];
    const fd = new FormData();
    files.forEach(f => fd.append("Dosyalar", f.file));
    const res = await postFormRequest<ApiResponse<{ yuklenenFotoUrlListesi: string[] }>>("/Fotograf/yukle", fd);
    if (!res.success || !res.data?.yuklenenFotoUrlListesi?.length) {
      throw new Error(res.errorDetail || `Adım ${stepIndex + 1} foto yükleme başarısız`);
    }
    return res.data.yuklenenFotoUrlListesi;
  };

  const deleteRemovedPhotos = async () => {
    if (!removedUrls.length) return;
    try {
      await postRequest<{ UrlList: string[] }, ApiResponse<string[]>>("/Fotograf/sil", { UrlList: removedUrls });
    } catch { /* opsiyonel */ }
  };

  /* ===========================
     Kaydet
  =========================== */
  const tagArray = useMemo(() => form.etiketlerInput
    .split(",").map(s => s.trim()).filter(Boolean), [form.etiketlerInput]);

  const validateForm = (): string | null => {
    if (!form.baslik.trim()) return "Başlık zorunlu.";
    return null;
  };

  const onSave = async () => {
    const err = validateForm();
    if (err) { alert(err); return; }

    try {
      // ---- 1) Ana Galeri yeni dosyaları yükle
      const galUploaded = await uploadGalleryNew();
      // ana galeri final list
      let galUploadIdx = 0;
      const finalGalList: GalleryImageJson[] = gallery.map((g, i) => {
        if (g.isNew) {
          const url = galUploaded[galUploadIdx++];
          return { url, alt: g.alt || "", siraNo: i, fit: g.fit };
        } else {
          return { url: g.url, alt: g.alt || "", siraNo: i, fit: g.fit };
        }
      });

      // Kapak belirle
      const finalGalUrls = finalGalList.map(x => x.url);
      let kapakUrl = "";
      if (coverIndex !== null && coverIndex >= 0 && coverIndex < finalGalUrls.length) kapakUrl = finalGalUrls[coverIndex];
      else if (finalGalUrls.length) kapakUrl = finalGalUrls[0];
      else if (form.kapakFotoUrl && isValidUrl(form.kapakFotoUrl)) kapakUrl = form.kapakFotoUrl;

      // ---- 2) Her adımın yeni dosyalarını yükle ve final yap
      const finalSteps: StepJson[] = [];
      for (let si = 0; si < steps.length; si++) {
        const step = steps[si];
        const uploaded = await uploadStepNew(si);
        let upIdx = 0;
        const imgs: StepImageJson[] = step.gallery.map((it, j) => {
          if (it.isNew) {
            const url = uploaded[upIdx++];
            return { url, alt: it.alt || "", siraNo: j, fit: it.fit };
          } else {
            return { url: it.url, alt: it.alt || "", siraNo: j, fit: it.fit };
          }
        });
        finalSteps.push({
          adim: step.adim?.trim() || "",
          aciklama: step.aciklama?.trim() || "",
          tarih: step.tarih || null,
          fotolar: imgs
        });
      }

      const payload = {
        Baslik: form.baslik.trim(),
        Aciklama: form.aciklama?.trim() || null,
        KapakFotoUrl: kapakUrl || null,
        FotoGaleriJson: stringify(finalGalList),
        YapimAsamalariJson: stringify(finalSteps),
        EtiketlerJson: stringify(tagArray),
        SiraNo: form.siraNo ?? 0,
        AktifMi: !!form.aktifMi
      };

      if (isEdit && form.id) {
        const res = await postRequest<any, ApiResponse<string>>("/Projeler/guncelle", { Id: form.id, ...payload });
        if (!res.success) throw new Error(res.errorDetail || "Güncelleme başarısız");
      } else {
        const res = await postRequest<any, ApiResponse<number>>("/Projeler/ekle", payload);
        if (!res.success) throw new Error(res.errorDetail || "Ekleme başarısız");
      }

      await deleteRemovedPhotos();

      setModalOpen(false);
      fetchList();
    } catch (e: any) {
      alert(e?.message || "Kaydetme hatası");
    }
  };

  /* ===========================
     Sil
  =========================== */
  const onDelete = async (id: number) => {
    if (!window.confirm("Bu projeyi silmek (pasif yapmak) istiyor musun?")) return;
    try {
      const res = await postRequest<{ id: number }, ApiResponse<string>>("/Projeler/sil", { id });
      if (!res.success) throw new Error(res.errorDetail || "Silme başarısız");
      fetchList();
    } catch (e: any) {
      alert(e?.message || "Silme hatası");
    }
  };

  /* ===========================
     UI
  =========================== */
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-5">
      {/* Header */}
      <div className="bg-white border rounded-lg shadow-sm p-3 sm:p-3.5 md:p-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 sm:gap-3">
          <div className="space-y-0.5">
            <h1 className="text-lg sm:text-xl md:text-2xl font-normal ">Projeler</h1>
            <p className="text-gray-500 text-xs sm:text-sm">Kapak, galeri, yapım aşamaları ve etiketlerle yönetin.</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 border rounded px-2 py-1">
              <Search className="w-4 h-4 text-gray-500" />
              <input
                className="outline-none text-xs sm:text-sm"
                placeholder="Ara (başlık/açıklama)…"
                value={q}
                onChange={e => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); fetchList(); } }}
              />
            </div>
            <select
              className="border rounded px-2 py-1 text-xs sm:text-sm"
              value={String(aktifFilter)}
              onChange={(e) => setAktifFilter(e.target.value === "null" ? null : e.target.value === "true")}
            >
              <option value="null">Tümü</option>
              <option value="true">Aktif</option>
              <option value="false">Pasif</option>
            </select>
            <select
              className="border rounded px-2 py-1 text-xs sm:text-sm"
              value={sort}
              onChange={e => setSort(e.target.value as any)}
            >
              <option value="yeni">En Yeni</option>
              <option value="eski">En Eski</option>
              <option value="sira">Sıra No</option>
            </select>
            <button
              onClick={() => { setPage(1); fetchList(); }}
              className="px-2.5 py-1.5 border rounded text-xs sm:text-sm hover:bg-gray-50"
            >Uygula</button>

            <button
              onClick={openAdd}
              className="w-full sm:w-auto flex items-center justify-center gap-1.5 sm:gap-2 bg-green-600 text-white px-3 sm:px-4 py-2 rounded hover:bg-green-700 text-sm"
            >
              <Plus className="w-4 h-4" /> Yeni Proje
            </button>
          </div>
        </div>
      </div>

      {/* Tablo */}
      <div className="bg-white border rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full text-xs sm:text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Kapak</th>
              <th className="px-3 py-2 text-left">Başlık</th>
              <th className="px-3 py-2 text-left">Açıklama</th>
              <th className="px-3 py-2 text-left">Aktif</th>
              <th className="px-3 py-2 text-left">Sıra</th>
              <th className="px-3 py-2 text-left">Oluşturma</th>
              <th className="px-3 py-2 text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan={7} className="p-4 text-center">Yükleniyor…</td></tr>
            ) : list.length === 0 ? (
              <tr><td colSpan={7} className="p-6 text-center text-gray-500">Kayıt yok</td></tr>
            ) : (
              list.map(it => (
                <tr key={it.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    {isValidUrl(it.kapakFotoUrl) ? (
                      <div className="w-14 h-10 bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                        <img src={it.kapakFotoUrl!} alt=""
                          className="w-full h-full object-contain" />
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 font-medium">{it.baslik}</td>
                  <td className="px-3 py-2 max-w-[280px] sm:max-w-[360px] truncate">{it.aciklama || "—"}</td>
                  <td className="px-3 py-2">{it.aktifMi ? "Evet" : "Hayır"}</td>
                  <td className="px-3 py-2">{it.siraNo}</td>
                  <td className="px-3 py-2">{new Date(it.olusturmaTarihi).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-2 sm:gap-3">
                      <button className="text-blue-600 hover:underline" onClick={() => openEdit(it)}>
                        <Edit2 className="w-4 h-4 inline-block mr-1" /> Düzenle
                      </button>
                      <button className="text-red-600 hover:underline" onClick={() => onDelete(it.id)}>
                        <Trash2 className="w-4 h-4 inline-block mr-1" /> Sil
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Sayfalama */}
      <div className="bg-white border rounded-lg shadow-sm p-2.5 sm:p-3 text-xs sm:text-sm flex items-center justify-between">
        <div>Toplam: <b>{total}</b></div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            disabled={!canPrev}
            onClick={() => canPrev && setPage(p => p - 1)}
            className={`px-2 py-1 border rounded ${canPrev ? "hover:bg-gray-50" : "opacity-50 cursor-not-allowed"}`}
          >Önceki</button>
          <span>Sayfa {page}/{totalPages}</span>
          <button
            disabled={!canNext}
            onClick={() => canNext && setPage(p => p + 1)}
            className={`px-2 py-1 border rounded ${canNext ? "hover:bg-gray-50" : "opacity-50 cursor-not-allowed"}`}
          >Sonraki</button>
          <select
            className="border rounded px-2 py-1"
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            {[10, 20, 50].map(n => <option key={n} value={n}>{n} / sayfa</option>)}
          </select>
        </div>
      </div>

      {/* ===== Modal (geniş) ===== */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={isEdit ? "Projeyi Düzenle" : "Yeni Proje"}
        size="xxxl"
        initialFocusRef={saveRef as any}
        footer={
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full">
            <div className="flex gap-1.5">
              <button
                onClick={() => setActiveTab("genel")}
                className={`px-2.5 py-1.5 rounded border text-xs sm:text-sm ${activeTab === "genel" ? "bg-blue-100 border-blue-300" : "hover:bg-gray-50"}`}
              >Genel</button>
              <button
                onClick={() => setActiveTab("galeri")}
                className={`px-2.5 py-1.5 rounded border text-xs sm:text-sm ${activeTab === "galeri" ? "bg-blue-100 border-blue-300" : "hover:bg-gray-50"}`}
              >Galeri</button>
              <button
                onClick={() => setActiveTab("asamalar")}
                className={`px-2.5 py-1.5 rounded border text-xs sm:text-sm ${activeTab === "asamalar" ? "bg-blue-100 border-blue-300" : "hover:bg-gray-50"}`}
              >Aşamalar</button>
            </div>
            <div className="flex gap-1.5">
              <button className="px-3 py-1.5 rounded border hover:bg-gray-50 text-xs sm:text-sm" onClick={closeModal}>Vazgeç</button>
              <button ref={saveRef} className="px-3 sm:px-4 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-xs sm:text-sm" onClick={onSave}>
                Kaydet
              </button>
            </div>
          </div>
        }
      >
        {/* İçerik alanı — mobil minimal padding */}
        <div className="pr-0 sm:pr-1">
          {/* === GENEL === */}
          {activeTab === "genel" && (
            <div className="space-y-4 sm:space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium">Başlık *</label>
                  <input
                    className="mt-1 w-full border rounded px-2 sm:px-3 py-1.5 sm:py-2 text-sm"
                    value={form.baslik}
                    onChange={e => setForm({ ...form, baslik: e.target.value })}
                    placeholder="Proje başlığı"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium">Sıra No</label>
                  <input
                    type="number"
                    className="mt-1 w-full border rounded px-2 sm:px-3 py-1.5 sm:py-2 text-sm"
                    value={form.siraNo}
                    onChange={e => setForm({ ...form, siraNo: Number(e.target.value) })}
                    placeholder="0"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs sm:text-sm font-medium">Açıklama</label>
                  <textarea
                    className="mt-1 w-full border rounded px-2 sm:px-3 py-2 min-h-[90px] sm:min-h-[100px] text-sm"
                    value={form.aciklama}
                    onChange={e => setForm({ ...form, aciklama: e.target.value })}
                    placeholder="Kısa proje açıklaması…"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="aktifMi"
                    type="checkbox"
                    checked={!!form.aktifMi}
                    onChange={e => setForm({ ...form, aktifMi: e.target.checked })}
                  />
                  <label htmlFor="aktifMi" className="text-xs sm:text-sm">Aktif (yayında)</label>
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs sm:text-sm font-medium">Etiketler (virgülle ayır)</label>
                  <input
                    className="mt-1 w-full border rounded px-2 sm:px-3 py-1.5 sm:py-2 text-sm"
                    value={form.etiketlerInput}
                    onChange={e => setForm({ ...form, etiketlerInput: e.target.value })}
                    placeholder="PLA, Prototip, Endüstriyel Tasarım"
                  />
                </div>
              </div>
            </div>
          )}

          {/* === GALERİ === */}
          {activeTab === "galeri" && (
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm sm:text-base">Proje Fotoğrafları</h3>
                <div className="text-xs text-gray-500">{gallery.length} görsel</div>
              </div>

              {/* Dropzone */}
              <div
                onClick={() => document.getElementById("galeri-file-input")?.click()}
                className="rounded border-2 border-dashed p-4 sm:p-5 text-center cursor-pointer hover:bg-gray-50"
              >
                <input
                  id="galeri-file-input" type="file" accept="image/*" multiple className="hidden"
                  onChange={e => onSelectGalleryFiles(e.target.files)}
                />
                <div className="flex flex-col items-center gap-1 text-gray-600">
                  <ImageIcon className="w-6 h-6" />
                  <div className="text-xs sm:text-sm">Görselleri sürükleyip bırakın veya tıklayıp seçin.</div>
                </div>
              </div>

              {/* Grid */}
              {gallery.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2.5 sm:gap-3">
                  {gallery.map((g, idx) => {
                    const imgSrc = g.isNew ? g.preview : g.url;
                    const isCover = coverIndex === idx;
                    return (
                      <div key={g.id} className="border rounded overflow-hidden bg-white">
                        <div className="w-full h-32 sm:h-40 bg-gray-100 flex items-center justify-center">
                          <img
                            src={imgSrc} alt=""
                            className={`w-full h-full ${g.fit === "cover" ? "object-cover" : "object-contain"}`}
                          />
                        </div>
                        <div className="p-2 space-y-2">
                          <input
                            className="w-full border rounded px-2 py-1 text-[11px] sm:text-xs"
                            placeholder="Alt metin"
                            value={g.alt}
                            onChange={e => {
                              const v = e.target.value;
                              setGallery(prev => {
                                const next = [...prev]; // @ts-ignore
                                next[idx].alt = v; return next;
                              });
                            }}
                          />
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                className={`text-[11px] px-1.5 py-0.5 rounded border flex items-center gap-1 ${isCover ? "bg-amber-100 border-amber-300 text-amber-800" : "hover:bg-gray-50"}`}
                                onClick={() => setCoverIndex(idx)}
                                title={isCover ? "Kapak" : "Kapağa ata"}
                              >
                                <Star className="w-3 h-3" /> {isCover ? "Kapak" : "Kapak yap"}
                              </button>
                              <button
                                type="button"
                                className="text-[11px] px-1.5 py-0.5 rounded border flex items-center gap-1 hover:bg-gray-50"
                                onClick={() => toggleGalleryFit(idx)}
                                title={gallery[idx].fit === "cover" ? "Contain'a geç" : "Cover'a geç"}
                              >
                                {gallery[idx].fit === "cover" ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                                {gallery[idx].fit === "cover" ? "Contain" : "Cover"}
                              </button>
                            </div>
                            <div className="flex items-center gap-1">
                              <button className="p-1 border rounded hover:bg-gray-50" onClick={() => moveGallery(idx, -1)} title="Sola">
                                <MoveLeft className="w-4 h-4" />
                              </button>
                              <button className="p-1 border rounded hover:bg-gray-50" onClick={() => moveGallery(idx, +1)} title="Sağa">
                                <MoveRight className="w-4 h-4" />
                              </button>
                              <button className="p-1 border rounded hover:bg-gray-50" onClick={() => removeGalleryItem(idx)} title="Kaldır">
                                <X className="w-4 h-4 text-red-500" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* === AŞAMALAR === */}
          {activeTab === "asamalar" && (
            <div className="space-y-4 sm:space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm sm:text-base">Yapım Aşamaları (çoklu foto)</h3>
                <button className="text-green-600 flex items-center gap-1 text-sm" onClick={addStep}>
                  <Plus className="w-4 h-4" /> Adım Ekle
                </button>
              </div>

              {steps.length === 0 && <p className="text-xs sm:text-sm text-gray-500">Henüz adım yok.</p>}
              {steps.map((s, si) => (
                <div key={si} className="border rounded-lg p-3 sm:p-4 space-y-3 bg-white">
                  <div className="flex items-center justify-between">
                    <div className="text-xs sm:text-sm font-medium">Adım {si + 1}</div>
                    <div className="flex items-center gap-1">
                      <button className="p-1 border rounded hover:bg-gray-50" onClick={() => moveStep(si, -1)} title="Yukarı">
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button className="p-1 border rounded hover:bg-gray-50" onClick={() => moveStep(si, +1)} title="Aşağı">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button className="p-1 border rounded hover:bg-gray-50" onClick={() => removeStep(si)} title="Kaldır">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 sm:gap-3">
                    <div>
                      <label className="block text-[11px] sm:text-xs font-medium">Adım Başlığı</label>
                      <input
                        className="mt-1 w-full border rounded px-2 py-1 text-sm"
                        value={s.adim}
                        onChange={e => updateStepField(si, "adim", e.target.value)}
                        placeholder="Örn: Tasarım"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] sm:text-xs font-medium">Tarih</label>
                      <input
                        type="date"
                        className="mt-1 w-full border rounded px-2 py-1 text-sm"
                        value={s.tarih || ""}
                        onChange={e => updateStepField(si, "tarih", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] sm:text-xs font-medium">Açıklama</label>
                      <input
                        className="mt-1 w-full border rounded px-2 py-1 text-sm"
                        value={s.aciklama}
                        onChange={e => updateStepField(si, "aciklama", e.target.value)}
                        placeholder="Kısa açıklama"
                      />
                    </div>
                  </div>

                  {/* Adım Foto Yükleme */}
                  <div
                    onClick={() => document.getElementById(`step-file-${si}`)?.click()}
                    className="rounded border-2 border-dashed p-3 sm:p-4 text-center cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      id={`step-file-${si}`} type="file" accept="image/*" multiple className="hidden"
                      onChange={e => onSelectStepFiles(si, e.target.files)}
                    />
                    <div className="text-xs sm:text-sm text-gray-600">Bu adıma foto eklemek için tıklayın veya sürükleyin.</div>
                  </div>

                  {/* Adım Foto Grid */}
                  {s.gallery.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2.5 sm:gap-3">
                      {s.gallery.map((g, gi) => {
                        const src = g.isNew ? g.preview : g.url;
                        return (
                          <div key={g.id} className="border rounded overflow-hidden bg-white">
                            <div className="w-full h-32 sm:h-40 bg-gray-100 flex items-center justify-center">
                              <img
                                src={src} alt=""
                                className={`w-full h-full ${g.fit === "cover" ? "object-cover" : "object-contain"}`}
                              />
                            </div>
                            <div className="p-2 space-y-2">
                              <input
                                className="w-full border rounded px-2 py-1 text-[11px] sm:text-xs"
                                placeholder="Alt metin"
                                value={g.alt}
                                onChange={e => {
                                  const v = e.target.value;
                                  setSteps(prev => {
                                    const next = [...prev];
                                    const gal = [...next[si].gallery]; // @ts-ignore
                                    gal[gi].alt = v; next[si] = { ...next[si], gallery: gal };
                                    return next;
                                  });
                                }}
                              />
                              <div className="flex items-center justify-between">
                                <button
                                  type="button"
                                  className="text-[11px] px-1.5 py-0.5 rounded border flex items-center gap-1 hover:bg-gray-50"
                                  onClick={() => toggleStepPhotoFit(si, gi)}
                                  title={s.gallery[gi].fit === "cover" ? "Contain'a geç" : "Cover'a geç"}
                                >
                                  {s.gallery[gi].fit === "cover" ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                                  {s.gallery[gi].fit === "cover" ? "Contain" : "Cover"}
                                </button>
                                <div className="flex items-center gap-1">
                                  <button className="p-1 border rounded hover:bg-gray-50" onClick={() => moveStepPhoto(si, gi, -1)} title="Sola">
                                    <MoveLeft className="w-4 h-4" />
                                  </button>
                                  <button className="p-1 border rounded hover:bg-gray-50" onClick={() => moveStepPhoto(si, gi, +1)} title="Sağa">
                                    <MoveRight className="w-4 h-4" />
                                  </button>
                                  <button className="p-1 border rounded hover:bg-gray-50" onClick={() => removeStepPhoto(si, gi)} title="Kaldır">
                                    <X className="w-4 h-4 text-red-500" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AdminProjelerPage;
